import React from 'react';
import { Node } from 'reactflow';
import { Button } from "@/shared/components/ui/button";
import { Loader2, Play, Copy } from "lucide-react";
import { Card } from "@/shared/components/ui/card";
import { JsonViewer } from "@/shared/components/ui/json-viewer";
import { useTranslation } from 'react-i18next';
import { useToast } from "@/shared/components/ui/use-toast";

interface NodeTestingComponentProps {
  node: Node;
  isRunning: boolean;
   // eslint-disable-next-line @typescript-eslint/no-explicit-any
  output: any;
  error: string | null;
  input: string;
  onTestNode: () => void;
}

export const NodeTestingComponent: React.FC<NodeTestingComponentProps> = ({
  node,
  isRunning,
  output,
  error,
  input,
  onTestNode
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  // Verifica se o input deve ser tratado como JSON
  const isJsonInput = node.data?.config?.input_format === 'json';
  
  // Estado para controlar se o JSON é válido
  const [isValidJson, setIsValidJson] = React.useState(true);
  
  // Valida o JSON quando o input mudar
  React.useEffect(() => {
    if (isJsonInput && input.trim()) {
      try {
        JSON.parse(input);
        setIsValidJson(true);
      } catch (error) {
        setIsValidJson(false);
      }
    }
  }, [input, isJsonInput]);

  // Função para copiar o output para a área de transferência
  const copyOutputToClipboard = () => {
    try {
      const outputText = typeof output === 'string' 
        ? output 
        : JSON.stringify(output, null, 2);
        
      navigator.clipboard.writeText(outputText);
      
      toast({
        title: t('agents.workflow.output.copied', 'Copiado!'),
        description: t('agents.workflow.output.copiedDescription', 'Output copiado para a área de transferência.'),
        duration: 2000,
      });
    } catch (error) {
      console.error('Erro ao copiar output:', error);
      
      toast({
        title: t('agents.workflow.output.copyError', 'Erro ao copiar'),
        description: t('agents.workflow.output.copyErrorDescription', 'Não foi possível copiar o output.'),
        variant: 'destructive',
        duration: 2000,
      });
    }
  };

  // Renderiza o output do teste
  const renderOutput = () => {
    if (!output && !error) {
      return (
        <div className="h-full flex flex-col items-center justify-center text-sm text-muted-foreground border rounded-md bg-muted/50 p-4">
          <span className="font-medium">{t('agents.workflow.noOutputTitle', 'Sem resultado')}</span>
          <span className="text-center mt-1">{t('agents.workflow.noOutputDesc', 'Execute o teste para ver o resultado')}</span>
        </div>
      );
    }

    if (error) {
      return (
        <Card className="bg-red-50 p-4 text-red-800 text-sm overflow-auto max-h-80">
          {error}
        </Card>
      );
    }

    return (
      <div className="bg-emerald-50 rounded-md">
        <JsonViewer
          data={output}
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: '0.875rem',
            overflowWrap: 'break-word'
          }}
        />
      </div>
    );
  };

  return (
    <div className="w-[350px] border-l pl-4 flex flex-col min-h-0">
      {/* Área de resultado */}
      <div className="flex-1 overflow-y-auto mb-4">
        <div className="flex items-center justify-between mb-1">
          <label className="text-sm font-medium">{t('agents.workflow.output', 'Resultado')}:</label>
          {output && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-5 w-5"
              onClick={copyOutputToClipboard}
              title={t('agents.workflow.output.copy', 'Copiar output')}
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        {renderOutput()}
      </div>
      
      {/* Botão de teste */}
      <div className="mt-4">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onTestNode}
          disabled={isRunning || (isJsonInput && !isValidJson)}
          className="w-full"
        >
          {isRunning ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              {t('agents.workflow.test.testing', 'Testando...')}
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-2" />
              {t('agents.workflow.test.title', 'Testar nó')}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default NodeTestingComponent;
